import { X } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { activeFacets, EMPTY_KG_FILTERS, removeValue, type KgFilters } from './filters'
import { useFacetLabel } from './useFacetLabel'

interface Props {
  filters: KgFilters
  onChange: (next: KgFilters) => void
}

/**
 * The active filters, OUTSIDE the popover: one removable chip per value and a
 * Clear All at the row's edge — the same row the Data Catalog and Tag Mapping
 * draw, so a reader can tell a narrowed graph from missing data without
 * reopening the menu. Renders nothing when no filter is on.
 */
const FilterChips = ({ filters, onChange }: Props) => {
  const { t } = useTranslation()
  const labelFor = useFacetLabel()
  const facets = activeFacets(filters)
  if (facets.length === 0) return null

  return (
    <div
      className="flex items-center justify-between gap-3 border-b border-border bg-muted/40 px-6 py-2"
      data-testid="kg-filter-chips"
    >
      <div className="flex flex-wrap items-center gap-2">
        {facets.map(({ facet, value }) => {
          const label = labelFor(facet, value)
          return (
            <span
              key={`${facet}:${value}`}
              className="inline-flex max-w-[260px] items-center gap-1.5 rounded-full border border-border bg-background py-1 pe-1.5 ps-2.5 text-xs font-medium text-foreground"
            >
              <span className="truncate">{label}</span>
              <button
                type="button"
                onClick={() => onChange(removeValue(filters, facet, value))}
                aria-label={t('data_catalog:filter.remove_chip', { defaultValue: 'Remove {{label}} filter', label })}
                className="grid size-4 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="size-3" />
              </button>
            </span>
          )
        })}
      </div>
      <button
        type="button"
        onClick={() => onChange(EMPTY_KG_FILTERS)}
        className="shrink-0 whitespace-nowrap text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        {t('data_catalog:filter.clear_all', { defaultValue: 'Clear All Filters' })}
      </button>
    </div>
  )
}

export default FilterChips
